import React, { useEffect, useState } from "react";
import BottomSheet from "../../ui/BottomSheet";
import { OptionsState } from "@/types/productModal";
import {
  EmployeeThatProvidesThisService,
  InCartSlot,
  OptionList,
} from "@/types/product";
import { SelectedOptions } from "@/types/cart";
import { useSelector } from "react-redux";
import {
  addItem,
  clearCart,
  validateCart,
  updateHasServiceInCart,
} from "@/store/features/cartSlice";
import { useDispatch } from "react-redux";
import Modal from "../../ui/Modal";
import ProductImageSection from "./ProductImageSection";
import ProductPriceSection from "./ProductPriceSection";
import ProductDescriptionSection from "./ProductDescriptionSection";
import ProductOptionsWithButton from "./ProductOptionsWithButton";

interface ProductDetailsProps {
  isModalOpen: boolean;
  handleModalClose: () => void;
  product: any;
}

const ProductDetails: React.FC<ProductDetailsProps> = ({
  isModalOpen,
  handleModalClose,
  product,
}) => {
  // Initialize Redux dispatch
  const dispatch = useDispatch();

  // State to store selected options for the product
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({});

  // State to manage the structure of each option list (options, types, etc.)
  const [selectionList, setSelectionList] = useState<OptionsState>({});

  // Extract option lists from product, defaulting to an empty array if undefined
  const [optionLists] = useState<OptionList[]>(
    product.active_option_lists || [],
  );

  // Manage quantity of the product
  const [quantity, setQuantity] = useState(1);

  // Validate cart to determine if we need to clear it before adding new items
  const validCart = useSelector(validateCart);

  // Handle increment/decrement of product quantity
  const handleQuantityChange = (increment: boolean) => {
    setQuantity((prevQuantity) => {
      if (increment) {
        // Check if product has limit_per_order (for daily specials)
        const limitPerOrder = product.limit_per_order;
        if (limitPerOrder && prevQuantity >= limitPerOrder) {
          return prevQuantity; // Don't exceed limit
        }
        return prevQuantity + 1;
      }
      return Math.max(prevQuantity - 1, 1);
    });
  };

  const [appointmentDuration, setAppointmentDuration] = useState(0);
  const [selectedAppointmentDateTime, setSelectedAppointmentDateTime] =
    useState<Date | null>(null);
  const [selectedServiceProvider, setSelectedServiceProvider] =
    useState<EmployeeThatProvidesThisService | null>(null);

  // Trigger when "Add to Cart" button is clicked
  const handleAddToCart = () => {
    // Create a product object which includes selected options
    const productToAdd = {
      ...product,
      unit_price: product.unit_price,
      quantity,
      selected_options: selectedOptions,
      uniqueId: "",
      appointment_duration: appointmentDuration,
      selected_appointment_date_time: selectedAppointmentDateTime,
      selected_service_provider: selectedServiceProvider,
    };

    // If cart is invalid or from a different branch, clear it first
    if (validCart) {
      dispatch(clearCart());
    }

    // Update the hasServiceInCart state
    dispatch(updateHasServiceInCart());

    // Add the product (with options) to the cart
    dispatch(addItem(productToAdd));

    // Close the modal after adding to cart
    handleModalClose();
  };

  // Check if all required selections are made
  const isValidSelection = () => {
    return Object.entries(selectionList).every(([_, list]) => {
      // If list is not required, no need to validate
      if (!list.required) return true;

      // For increment_decrement type, verify at least one option is selected
      if (list.type === "increment_decrement") {
        return Object.values(list.options).some(
          (option) => option.quantity > 0,
        );
      }
      // For multiple_choice or checkbox, verify at least one option is selected
      else if (list.type === "multiple_choice" || list.type === "checkbox") {
        return Object.values(list.options).some((option) => option.selected);
      }
      // Default case: assume valid
      return true;
    });
  };

  // Calculate the total price based on base price, option surcharges, and quantity
  const calculateTotal = () => {
    // Base price of the product
    const baseTotal = parseFloat(product?.unit_price ?? "0.00");

    // Sum of surcharges from selected options
    const optionsTotal = Object.entries(selectionList).reduce(
      (total, [_, currentList]) =>
        total +
        Object.entries(currentList.options).reduce(
          (optTotal, [__, currentOption]) =>
            optTotal + currentOption.surcharge * currentOption.quantity,
          0,
        ),
      0,
    );

    // Multiply by selected quantity, format to two decimal places
    return ((baseTotal + optionsTotal) * quantity).toFixed(2);
  };

  // Helper to check if surcharge should be displayed (non-zero)
  const shouldShowSurcharge = (surcharge: number): boolean => {
    const parsed = parseFloat(surcharge.toString());
    return !isNaN(parsed) && parsed > Number.EPSILON;
  };

  // Initialize selectionList state with product option lists
  useEffect(() => {
    if (optionLists.length > 0) {
      // Build an initial object structure based on the option lists
      const initialState = optionLists.reduce((acc, list, index) => {
        acc[index] = {
          id: list.id.toString(),
          required: list.required,
          name: list.name,
          maxSelection: list.max_selections,
          type: list.option_type,
          options: list.options.reduce(
            (optAcc, option) => {
              optAcc[option.id] = {
                id: option.id.toString(),
                name: option.name,
                quantity: 0,
                selected: false,
                surcharge: parseFloat(option.surcharge),
              };
              return optAcc;
            },
            {} as {
              [key: number]: {
                id: string;
                name: string;
                quantity: number;
                selected: boolean;
                surcharge: number;
              };
            },
          ),
        };
        return acc;
      }, {} as OptionsState);
      setSelectionList(initialState);
    }
  }, [optionLists]);

  // Handle user interactions with options (increment, decrement, select, unselect)
  const handleOptionChange = (
    listIndex: string,
    optionId: string,
    increment: boolean,
  ) => {
    const newSelectionList = { ...selectionList };
    const optionList = newSelectionList[listIndex];
    let doReRender = false; // helps prevent unnecessary rerenders
    const newSelectedOptions = { ...selectedOptions };

    if (!newSelectedOptions[optionId]) {
      newSelectedOptions[optionId] = {
        name: optionList.options[optionId].name,
        quantity: 0,
        selected: false,
        surcharge: optionList.options[optionId].surcharge,
      };
    }
    if (newSelectionList[listIndex].type === "increment_decrement") {
      const totalSelected = Object.entries(optionList.options).reduce(
        (total, [_, opt]) => total + opt.quantity,
        0,
      );

      if (
        increment &&
        (!optionList.maxSelection || totalSelected < optionList.maxSelection)
      ) {
        newSelectionList[listIndex].options[optionId].quantity += 1;
        newSelectedOptions[optionId].quantity += 1;
        newSelectedOptions[optionId].selected = true;
        doReRender = true;
      } else if (!increment) {
        if (newSelectionList[listIndex].options[optionId].quantity > 0) {
          newSelectionList[listIndex].options[optionId].quantity -= 1;
          newSelectedOptions[optionId].quantity -= 1;
          if (newSelectedOptions[optionId].quantity === 0) {
            newSelectedOptions[optionId].selected = false;
          } else {
            newSelectedOptions[optionId].selected = true;
          }
          doReRender = true;
        }
      }
    } else if (newSelectionList[listIndex].type === "multiple_choice") {
      Object.entries(optionList.options).forEach(
        ([currentOptionId, currentOption]) => {
          if (!newSelectedOptions[currentOptionId]) {
            newSelectedOptions[currentOptionId] = {
              name: optionList.options[currentOptionId].name,
              quantity: 0,
              selected: false,
              surcharge: optionList.options[currentOptionId].surcharge,
            };
          }
          if (optionList.options[currentOptionId].quantity > 0) {
            if (newSelectedOptions[currentOptionId].quantity > 1) {
              newSelectedOptions[currentOptionId].quantity -= 1; // Selected Options that helps in calculation
            } else {
              newSelectedOptions[currentOptionId].quantity = 0;
              newSelectedOptions[currentOptionId].selected = false;
            }
          }
          optionList.options[currentOptionId].quantity = 0; // Option List that renders the option
          optionList.options[currentOptionId].selected = false;
        },
      );
      optionList.options[optionId].quantity = 1;
      optionList.options[optionId].selected = true;
      newSelectedOptions[optionId].quantity += 1;
      newSelectedOptions[optionId].selected = true;
      doReRender = true;
    } else if (newSelectionList[listIndex].type === "checkbox") {
      const totalSelected = Object.entries(optionList.options).reduce(
        (total, [_, opt]) => total + (opt.selected ? 1 : 0),
        0,
      );
      if (increment) {
        if (
          !optionList.maxSelection ||
          totalSelected < optionList.maxSelection
        ) {
          newSelectionList[listIndex].options[optionId].quantity = 1;
          newSelectionList[listIndex].options[optionId].selected = true;
          newSelectedOptions[optionId].quantity += 1;
          newSelectedOptions[optionId].selected = true;
          doReRender = true;
        }
      } else {
        newSelectionList[listIndex].options[optionId].quantity = 0;
        newSelectionList[listIndex].options[optionId].selected = false;
        newSelectedOptions[optionId].quantity -= 1;
        newSelectedOptions[optionId].selected = false;
        doReRender = true;
      }
    }
    if (doReRender) {
      setSelectionList(newSelectionList);
      setSelectedOptions(newSelectedOptions);
    }
  };

  return (
    <div>
      {/* Desktop view (Modal) */}
      <div className="hidden md:block">
        <Modal visible={isModalOpen} onClose={handleModalClose}>
          <div className="flex flex-row justify-between h-full mb-4 p-8 gap-4">
            <div className="flex-1 overflow-y-auto flex flex-col items-center">
              <div className="w-[60%]">
                <ProductImageSection
                  product={product}
                  handleModalClose={handleModalClose}
                />
                <ProductDescriptionSection product={product} />
                <ProductPriceSection product={product} />
              </div>
            </div>
            {/* Right column: Options and actions */}
            <div className="flex-1 ml-0">
              <ProductOptionsWithButton
                selectionList={selectionList}
                shouldShowSurcharge={shouldShowSurcharge}
                handleOptionChange={handleOptionChange}
                quantity={quantity}
                handleQuantityChange={handleQuantityChange}
                calculateTotal={calculateTotal}
                isValidSelection={isValidSelection}
                handleAddToCart={handleAddToCart}
                product={product}
                appointmentDuration={appointmentDuration}
                setAppointmentDuration={setAppointmentDuration}
                selectedAppointmentDateTime={selectedAppointmentDateTime}
                setSelectedAppointmentDateTime={setSelectedAppointmentDateTime}
                selectedServiceProvider={selectedServiceProvider}
                setSelectedServiceProvider={setSelectedServiceProvider}
              />
            </div>
          </div>
        </Modal>
      </div>

      {/* Mobile view (BottomSheet) */}
      <div className="md:hidden">
        <BottomSheet
          isOpen={isModalOpen}
          onClose={handleModalClose}
          customStyles={{
            maxHeight: "93vh",
            overflowY: "hidden",
          }}
        >
          <div className="flex flex-col justify-between h-full pb-4">
            {/* Image and close button at the top */}
            <ProductImageSection
              product={product}
              handleModalClose={handleModalClose}
            />
            {/* Display base price below the image */}
            <ProductPriceSection product={product} />
            {/* Description and option selection below */}
            <div className="flex-1 overflow-y-auto">
              <ProductOptionsWithButton
                selectionList={selectionList}
                shouldShowSurcharge={shouldShowSurcharge}
                handleOptionChange={handleOptionChange}
                quantity={quantity}
                handleQuantityChange={handleQuantityChange}
                calculateTotal={calculateTotal}
                isValidSelection={isValidSelection}
                handleAddToCart={handleAddToCart}
                product={product}
                appointmentDuration={appointmentDuration}
                setAppointmentDuration={setAppointmentDuration}
                selectedAppointmentDateTime={selectedAppointmentDateTime}
                setSelectedAppointmentDateTime={setSelectedAppointmentDateTime}
                selectedServiceProvider={selectedServiceProvider}
                setSelectedServiceProvider={setSelectedServiceProvider}
              />
            </div>
          </div>
        </BottomSheet>
      </div>
    </div>
  );
};

export default ProductDetails;
